
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CheckCircle, Download, RefreshCw, Database } from 'lucide-react';
import { listPaymentBackups, restoreFromLocalPaymentBackup } from '@/services/backup/paymentBackup';

type BackupItem = {
  id: string;
  timestamp: string;
  size: number;
  description?: string;
  teacherPaymentsCount?: number;
  studentPaymentsCount?: number;
};

const PaymentBackupList = ({ onBackupRestored }: { onBackupRestored?: () => void }) => {
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  
  useEffect(() => {
    loadBackups();
  }, []);
  
  const loadBackups = async () => {
    setLoading(true);
    try {
      const backupsList = await listPaymentBackups();
      // Convert the returned objects to include the expected properties
      const formattedBackups: BackupItem[] = backupsList.map(backup => ({
        id: backup.id,
        timestamp: backup.timestamp,
        size: backup.size,
        description: backup.description,
        teacherPaymentsCount: backup.teacherPaymentsCount || 0,
        studentPaymentsCount: backup.studentPaymentsCount || 0
      }));
      setBackups(formattedBackups);
    } catch (error) {
      console.error('Failed to load payment backups:', error);
      toast.error('Failed to load payment backups');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRestore = async (backupId: string) => {
    setRestoring(backupId);
    try {
      const success = await restoreFromLocalPaymentBackup(backupId);
      if (success) {
        toast.success('Payment backup restored successfully');
        if (onBackupRestored) {
          onBackupRestored();
        }
      }
    } catch (error) {
      console.error('Failed to restore payment backup:', error);
      toast.error('Failed to restore payment backup');
    } finally {
      setRestoring(null);
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };
  
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
        <span className="text-sm text-muted-foreground">Loading payment backups...</span>
      </div>
    );
  }
  
  if (backups.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-muted-foreground">No payment backups found</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3 mt-2">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Payment Backup History</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8"
          onClick={loadBackups}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          <span>Refresh</span>
        </Button>
      </div>
      
      {backups.map(backup => (
        <motion.div
          key={backup.id}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="overflow-hidden">
            <CardContent className="p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium">{formatDate(backup.timestamp)}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(backup.size)}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      Teachers: {backup.teacherPaymentsCount || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Students: {backup.studentPaymentsCount || 0}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={Boolean(restoring)}
                    onClick={() => handleRestore(backup.id)}
                    className="h-8 text-xs"
                  >
                    {restoring === backup.id ? (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        <span>Restoring...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        <span>Restore</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      navigator.clipboard.writeText(backup.id);
                      toast.success('Backup ID copied to clipboard');
                    }}
                    title="Copy Backup ID"
                  >
                    <Database className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};

export default PaymentBackupList;
